/**
 * Derives Usage & Billing dashboard copy from the `/usage` API fields.
 *
 * Backend returns `billable_minutes` (weighted billing units) and `free_minutes`
 * where allowance is denominated as 2 vCPU‑equivalent minutes (see GithubAppInstallation).
 */
export type MonthlyBillingUsagePresentation = {
  billableWeightedMinutes: number;
  /** Monthly free allowance (`free_minutes` from API) in 2 vCPU‑equivalent minutes */
  freeTierAllowance2vCpuMinutes: number;
  /** floor(billable / 2), matching the legacy weighted-minute display */
  totalNormalizedFloor: number;
  /** Minutes attributed to free tier so far this month */
  freeTierConsumed2vCpuMinutes: number;
  /** Estimated billable‑beyond‑allowance, expressed in normalized minutes */
  normalizedMinutesBeyondAllowanceFloor: number;
};

export function getMonthlyBillingUsagePresentation(
  billableWeightedMinutes: number,
  freeTierAllowance2vCpuMinutes: number,
): MonthlyBillingUsagePresentation {
  const billable = Math.max(0, billableWeightedMinutes);
  const allowance2vCpu = Math.max(0, freeTierAllowance2vCpuMinutes);
  const allowanceWeighted = allowance2vCpu * 2;
  const consumedWeighted = Math.min(billable, allowanceWeighted);
  const beyondWeighted = Math.max(0, billable - allowanceWeighted);

  return {
    billableWeightedMinutes: billable,
    freeTierAllowance2vCpuMinutes: allowance2vCpu,
    totalNormalizedFloor: Math.floor(billable / 2),
    freeTierConsumed2vCpuMinutes: Math.floor(consumedWeighted / 2),
    normalizedMinutesBeyondAllowanceFloor: Math.floor(beyondWeighted / 2),
  };
}
